import { useState } from 'react'

type ResultState = 'success' | 'failure' | undefined

/**
 * The confirm, success and failure steps of a `ConfirmationResultModal`. `dismiss` closes the modal, and
 * reports `onSuccess` first when the action succeeded; it does nothing while `isBusy`.
 */
export function useConfirmationResult({
  onClose,
  onSuccess,
  isBusy
}: {
  onClose: () => void
  onSuccess?: () => void
  isBusy: boolean
}) {
  const [state, setState] = useState<ResultState>()
  const [error, setError] = useState<unknown>()

  const succeed = () => setState('success')
  const fail = (cause: unknown) => {
    setError(cause)
    setState('failure')
  }

  return {
    state,
    error,
    succeed,
    fail,
    /** Runs the action, then shows its success or its failure; answers whether it succeeded. */
    run: async (action: () => Promise<unknown>) => {
      try {
        await action()
        succeed()

        return true
      } catch (cause) {
        fail(cause)

        return false
      }
    },
    backToConfirm: () => {
      setError(undefined)
      setState(undefined)
    },
    dismiss: () => {
      if (isBusy) return

      if (state === 'success') onSuccess?.()
      onClose()
    }
  }
}
